import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-bg-base py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-text-secondary hover:text-gold transition-colors mb-8">
          <ArrowLeft size={20} />
          Back to Home
        </Link>
        
        <div className="bg-bg-surface rounded-2xl p-8 lg:p-12 shadow-sm border border-border">
          <h1 className="font-display text-4xl font-bold text-text-primary mb-4">Terms of Service</h1>
          <p className="text-text-tertiary text-sm mb-8">Last updated: April 6, 2026</p>
          
          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">1. Acceptance of Terms</h2>
              <p className="text-text-secondary leading-relaxed">
                By accessing and using Daypop ("the Service"), you accept and agree to be bound by the terms and 
                provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">2. Use License</h2>
              <p className="text-text-secondary leading-relaxed mb-4">
                Permission is granted to temporarily access Daypop for personal, non-commercial transitory viewing only. 
                This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose</li>
                <li>Attempt to decompile or reverse engineer any software contained on Daypop</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
                <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">3. User Accounts</h2>
              <p className="text-text-secondary leading-relaxed">
                You are responsible for maintaining the confidentiality of your account and password. You agree to accept 
                responsibility for all activities that occur under your account or password. Daypop reserves the right 
                to refuse service, terminate accounts, or remove content in its sole discretion.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">4. User Content</h2>
              <p className="text-text-secondary leading-relaxed mb-4">
                You retain all rights to your content (journal entries, goals, ideas, etc.). By posting content on 
                Daypop, you grant us a license to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li>Store and display your content</li>
                <li>Process your content for service functionality (e.g., AI insights)</li>
                <li>Back up your content for reliability</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                You represent that you own or have the necessary rights to all content you submit.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">5. Privacy</h2>
              <p className="text-text-secondary leading-relaxed">
                Your use of Daypop is also governed by our Privacy Policy. Please review our 
                <Link to="/privacy" className="text-gold hover:text-gold-muted transition-colors"> Privacy Policy</Link>, 
                which also governs the Service and informs users of our data collection practices.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">6. Prohibited Uses</h2>
              <p className="text-text-secondary leading-relaxed mb-4">
                You may not use Daypop to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li>Violate any laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Transmit malicious code or viruses</li>
                <li>Spam, phish, or engage in any fraudulent activity</li>
                <li>Scrape or harvest data without permission</li>
                <li>Impersonate another person or entity</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">7. Disclaimer</h2>
              <p className="text-text-secondary leading-relaxed">
                The materials on Daypop are provided on an 'as is' basis. Daypop makes no warranties, expressed or 
                implied, and hereby disclaims and negates all other warranties including, without limitation, implied 
                warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of 
                intellectual property or other violation of rights.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">8. Limitations</h2>
              <p className="text-text-secondary leading-relaxed">
                In no event shall Daypop or its suppliers be liable for any damages (including, without limitation, 
                damages for loss of data or profit, or due to business interruption) arising out of the use or inability 
                to use Daypop, even if Daypop or an authorized representative has been notified orally or in writing 
                of the possibility of such damage.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">9. Service Modifications</h2>
              <p className="text-text-secondary leading-relaxed">
                Daypop reserves the right to modify or discontinue, temporarily or permanently, the Service (or any 
                part thereof) with or without notice. You agree that Daypop shall not be liable to you or to any 
                third party for any modification, suspension, or discontinuance of the Service.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">10. Termination</h2>
              <p className="text-text-secondary leading-relaxed">
                We may terminate or suspend your account and bar access to the Service immediately, without prior notice 
                or liability, under our sole discretion, for any reason whatsoever, including without limitation if you 
                breach the Terms. Upon termination, your right to use the Service will immediately cease.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">11. Governing Law</h2>
              <p className="text-text-secondary leading-relaxed">
                These Terms shall be governed and construed in accordance with the laws of the United States, without 
                regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms 
                will not be considered a waiver of those rights.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">12. Changes to Terms</h2>
              <p className="text-text-secondary leading-relaxed">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision 
                is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes 
                a material change will be determined at our sole discretion.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">13. Contact Us</h2>
              <p className="text-text-secondary leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-gold font-medium mt-2">support@daypop.online</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
